import React, { useCallback, useEffect, useRef } from 'react';
import { captureException } from '@/utils/sentry';
import { HostToSalsaMessage } from '@/generated/salsa-protocol';
import {
  MicroProductWebViewProps,
  MicroProductWebViewRef,
  SalsaMessage,
  SalsaMessageType,
} from '@domain/microproducts/components/micro-product-webview-types';
import { StyleSheet } from 'react-native';

export const MicroProductWebView = React.forwardRef<
  MicroProductWebViewRef,
  MicroProductWebViewProps
>(
  (
    {
      onAuthTokenRequest,
      onNavigateToHost,
      onNavigateToMicroproduct,
      onRouteChanged,
      onSidebarToggle,
      sidebarState,
      supportedHostRoutes,
      uri,
    },
    ref,
  ) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const hasLoadedRef = useRef(false);

    React.useImperativeHandle(ref, () => ({
      sendMessage: (message: HostToSalsaMessage) => {
        const salsaOrigin = new URL(uri).origin;
        iframeRef.current?.contentWindow?.postMessage(message, salsaOrigin);
      },
    }));

    useEffect(() => {
      // Normalize to origin (strips path/trailing slash) so comparison is robust
      const salsaOrigin = new URL(uri).origin;

      const handleMessage = async (event: MessageEvent) => {
        // Only accept messages from the Salsa origin
        if (event.origin !== salsaOrigin) {
          return;
        }

        const message = event.data as SalsaMessage;
        if (!message || typeof message.type !== 'string') {
          return;
        }

        switch (message.type) {
          case SalsaMessageType.AUTH_TOKEN_REQUEST: {
            const { requestId } = message;
            try {
              const token = await onAuthTokenRequest?.();
              if (token !== undefined) {
                iframeRef.current?.contentWindow?.postMessage(
                  {
                    requestId,
                    token,
                    type: SalsaMessageType.AUTH_TOKEN_RESPONSE,
                  },
                  salsaOrigin,
                );
              }
            } catch (err) {
              captureException(err);
            }
            break;
          }
          case SalsaMessageType.ERROR:
            captureException(message.error, {
              extra: { context: message.context },
            });
            break;
          case SalsaMessageType.NAVIGATE_MICROPRODUCT:
            onNavigateToMicroproduct?.({
              params: message.params,
              productId: message.productId,
            });
            break;
          case SalsaMessageType.NAVIGATE_HOST:
            onNavigateToHost?.({
              params: message.params,
              route: message.route,
            });
            break;
          case SalsaMessageType.ROUTE_CHANGED:
            onRouteChanged?.({
              replace: message.replace,
              route: message.route,
            });
            break;
          case SalsaMessageType.SIDEBAR_TOGGLE:
            onSidebarToggle?.();
            break;
          default:
            break;
        }
      };

      window.addEventListener('message', handleMessage);
      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }, [
      onAuthTokenRequest,
      onNavigateToHost,
      onNavigateToMicroproduct,
      onRouteChanged,
      onSidebarToggle,
      uri,
    ]);

    const sendCapabilities = useCallback(() => {
      hasLoadedRef.current = true;
      const contentWindow = iframeRef.current?.contentWindow;
      if (!contentWindow) {
        return;
      }
      const salsaOrigin = new URL(uri).origin;
      if (supportedHostRoutes) {
        contentWindow.postMessage(
          {
            supportedRoutes: supportedHostRoutes,
            type: SalsaMessageType.HOST_CAPABILITIES,
          },
          salsaOrigin,
        );
      }
      if (sidebarState) {
        contentWindow.postMessage(
          {
            isCollapsed: sidebarState.isCollapsed,
            isDrawerLayout: sidebarState.isDrawerLayout,
            type: SalsaMessageType.SIDEBAR_STATE,
          },
          salsaOrigin,
        );
      }
    }, [sidebarState, supportedHostRoutes, uri]);

    // Push state changes to the iframe after it's loaded. The initial
    // state is pushed alongside HOST_CAPABILITIES via `sendCapabilities`.
    useEffect(() => {
      if (!hasLoadedRef.current || !sidebarState) {
        return;
      }
      const contentWindow = iframeRef.current?.contentWindow;
      if (!contentWindow) {
        return;
      }
      const salsaOrigin = new URL(uri).origin;
      contentWindow.postMessage(
        {
          isCollapsed: sidebarState.isCollapsed,
          isDrawerLayout: sidebarState.isDrawerLayout,
          type: SalsaMessageType.SIDEBAR_STATE,
        },
        salsaOrigin,
      );
    }, [sidebarState, uri]);

    return (
      <iframe
        height="100%"
        onLoad={sendCapabilities}
        ref={iframeRef}
        referrerPolicy="strict-origin-when-cross-origin"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
        src={uri}
        style={styles.iframe}
        title="salsa-microproduct"
        width="100%"
      />
    );
  },
);

MicroProductWebView.displayName = 'MicroProductWebView';

const styles = StyleSheet.create({
  iframe: {
    borderWidth: 0,
    flex: 1,
  },
});
